import React from 'react';
import { FaRegEnvelope, FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getAuthorData } from '../../data/Authors';
import {
    SITE_BASE_URL,
    CANONICAL_BLOG_BASE,
    PUBLIC_BLOG_BASE,
} from '../../config/siteConfig';

const AuthorBox = styled.div`
    background: ${(props) =>
        props.theme.mode === 'light' ? '#f8f9fa' : '#2a2a2a'};
    border-radius: 12px;
    padding: 2rem;
    margin: 4rem 0;
    border-left: 4px solid ${(props) => props.theme.colors.primary};
    display: flex;
    gap: 2rem;
    align-items: flex-start;

    @media (max-width: 725px) {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }
`;

const AuthorImage = styled.img`
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    border: 3px solid ${(props) => props.theme.colors.primary};
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
`;

const AuthorImageLink = styled.a`
    display: block;
    flex-shrink: 0;
`;

const AuthorContent = styled.div`
    flex: 1;
`;

const AuthorTitle = styled.h3`
    margin: 0 0 0.5rem 0;
    font-size: 1.5rem;
    color: ${(props) => props.theme.body.headers};
    font-weight: 600;

    a {
        color: ${(props) => props.theme.body.headers} !important;
        text-decoration: none;
        transition: all 0.2s ease;

        &:hover {
            color: ${(props) => props.theme.colors.primary} !important;
        }
    }
`;

const AuthorRole = styled.p`
    margin: 0 0 1rem 0;
    color: ${(props) =>
        props.theme.mode === 'light' ? props.theme.colors.primary : '#10b981'};
    font-weight: 600;
    font-size: 1rem;
    ${(props) =>
        props.theme.mode === 'light'
            ? `-webkit-text-stroke: 0.5px rgba(0, 0, 0, 0.3);`
            : ''}
`;

const AuthorBio = styled.p`
    margin: 0 0 1rem 0;
    line-height: 1.6;
    color: ${(props) => props.theme.body.text};
    font-size: 0.95rem;
`;

const AuthorContact = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: ${(props) => props.theme.body.text} !important;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.6;
        text-decoration: underline;
    }

    /* Ensure react-icons SVGs inherit color and are visible */
    svg,
    svg * {
        fill: currentColor !important;
        stroke: currentColor !important;
    }

    svg {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
    }

    .icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: ${(props) => props.theme.colors.primary} !important;
        width: 1.2rem;
        height: 1.2rem;
        flex-shrink: 0;
    }

    .icon svg,
    .icon svg * {
        fill: currentColor !important;
        stroke: currentColor !important;
    }

    .icon svg {
        width: 100%;
        height: 100%;
        display: block;
    }

    .contactText {
        margin-left: 0.25rem;
    }
`;

const ContactWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    @media (max-width: 725px) {
        align-items: center;
    }
`;

const WhatsAppContact = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: ${(props) => props.theme.body.text} !important;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.6;
        text-decoration: underline;
    }

    svg,
    svg * {
        fill: currentColor !important;
        stroke: currentColor !important;
    }

    svg {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
    }

    .icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: ${(props) => props.theme.colors.primary} !important;
        width: 1.2rem;
        height: 1.2rem;
        flex-shrink: 0;
    }

    .icon svg,
    .icon svg * {
        fill: currentColor !important;
        stroke: currentColor !important;
    }

    .icon svg {
        width: 100%;
        height: 100%;
        display: block;
    }

    .contactText {
        margin-left: 0.25rem;
    }
`;

/**
 * Author Component with SEO Schema
 * Now receives only authorId and fetches data from Authors.js
 */
const Author = ({ authorId = 'carolina-mendez' }) => {
    const { t, i18n } = useTranslation();

    // Get author data for current language
    const author = getAuthorData(authorId, i18n.language);

    // If author not found, don't render anything
    if (!author) {
        console.warn(`Author with id "${authorId}" not found`);
        return null;
    }

    const { name, image, email, whatsapp, jobTitle, bio, id, social } = author;
    const firstName = name.split(' ')[0];
    const basePath = PUBLIC_BLOG_BASE;
    const language = i18n.language || 'en';
    const profilePath = `/${language}/guias/${id}`;
    const profileUrl = `${basePath}${profilePath}`;
    const profileCanonicalUrl = `${CANONICAL_BLOG_BASE}${profilePath}`;

    // Generate Person Schema for SEO
    const personSchema = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: name,
        jobTitle: jobTitle,
        description: bio,
        email: email,
        url: profileCanonicalUrl,
        image: image,
        worksFor: {
            '@type': 'Organization',
            name: 'Dizen',
            url: SITE_BASE_URL,
        },
        sameAs: social ? Object.values(social) : [],
    };

    return (
        <>
            <AuthorBox>
                <AuthorImageLink
                    href={profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <AuthorImage
                        src={image}
                        alt={`${name} - ${jobTitle}`}
                        loading="lazy"
                    />
                </AuthorImageLink>
                <AuthorContent>
                    <AuthorTitle>
                        <a
                            href={profileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('author.writtenBy')} {name}
                        </a>
                    </AuthorTitle>
                    <AuthorRole>{jobTitle}</AuthorRole>
                    <AuthorBio>{bio}</AuthorBio>
                    <ContactWrapper>
                        <AuthorContact href={`mailto:${email}`}>
                            <span className="icon" aria-hidden="true">
                                <FaRegEnvelope size={18} />
                            </span>
                            <span className="contactText">
                                {t('author.sendEmail')} {firstName}
                            </span>
                        </AuthorContact>
                        {whatsapp && (
                            <WhatsAppContact
                                href={`https://wa.me/${whatsapp}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span className="icon" aria-hidden="true">
                                    <FaWhatsapp size={18} />
                                </span>
                                <span className="contactText">
                                    {t('author.sendWhatsApp')} {firstName}
                                </span>
                            </WhatsAppContact>
                        )}
                    </ContactWrapper>
                </AuthorContent>
            </AuthorBox>

            {/* Person Schema for SEO */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(personSchema),
                }}
            />
        </>
    );
};

export default Author;
